import {
  Alert02Icon,
  ArrowLeft01Icon,
  Refresh01Icon,
} from "@hugeicons/core-free-icons";
import { useLocation } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { AppLink } from "@/components/common/app-link";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Aux0ApiError, UnavailableDataError } from "@/data";
import { cn } from "@/lib/utils";
import { Icon } from "./icon";

/**
 * The three states every screen has besides its happy one, in one place so
 * they do not get invented per screen.
 *
 * The Next app had none of them: a page shell read a fixture synchronously, so
 * an empty list rendered an empty panel, a thrown read rendered the framework's
 * error overlay, and there was no loading state at all because there was
 * nothing to wait for. With a data adapter all three are real, and all three
 * are things a customer will see the first time a provider is slow.
 */

/**
 * Nothing to show. The distinction that matters: an empty list because the
 * customer has not done the thing yet needs an action, and an empty list
 * because a filter excluded everything needs a way back — never the same
 * words.
 */
export function EmptyState({
  title,
  description,
  action,
  className,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center gap-2.5 px-6 py-14 text-center",
        className,
      )}
    >
      <p className="text-[14px] font-medium">{title}</p>
      {description ? (
        <p className="max-w-[46ch] text-[13px] leading-[1.55] text-muted-foreground">
          {description}
        </p>
      ) : null}
      {action ? <div className="mt-1.5">{action}</div> : null}
    </div>
  );
}

/**
 * A read failed.
 *
 * It names the resource, shows the message, and offers the one action that
 * might help. It does not apologise and it does not say "something went
 * wrong": a customer with a failing endpoint needs the text their support
 * ticket will quote.
 */
export function ErrorState({
  title = "That did not load",
  error,
  onRetry,
  className,
}: {
  title?: string;
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const { pathname } = useLocation();
  const project = pathname.match(/^\/p\/([^/]+)/)?.[1];
  if (error instanceof UnavailableDataError) {
    return (
      <div
        role="status"
        className="flex flex-col items-center gap-4 px-6 py-14 text-center"
      >
        <h1 className="text-[22px] font-semibold">Not available yet</h1>
        <p className="max-w-[55ch] text-[13px] text-muted-foreground">
          {error.message}
        </p>
        <p className="text-[13px] text-muted-foreground">
          No demo data is shown in this view.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          {project ? (
            ["accounts", "keys", "logs"].map((path) => (
              <Button key={path} variant="outline" size="sm" asChild>
                <AppLink to={`/p/${project}/${path}`}>
                  {path === "keys" ? "API keys" : path === "logs" ? "Logs" : "Accounts"}
                </AppLink>
              </Button>
            ))
          ) : (
            <Button asChild size="sm">
              <AppLink to="/signed-in">Open account setup</AppLink>
            </Button>
          )}
        </div>
      </div>
    );
  }
  const status = error instanceof Aux0ApiError ? error.status : undefined;
  const heading =
    status === 403 ? "Access denied" : status === 404 ? "Resource not found" : title;
  const message =
    error instanceof Error ? error.message : "The request failed with no message.";
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center gap-3 px-6 py-14 text-center",
        className,
      )}
    >
      <span
        aria-hidden
        className="flex size-9 items-center justify-center rounded-full bg-stop-soft text-stop"
      >
        <Icon icon={Alert02Icon} className="size-4.5" />
      </span>
      <div className="flex flex-col gap-1.5">
        <p className="text-[14px] font-medium">{heading}</p>
        <p className="max-w-[62ch] font-mono text-[12px] leading-[1.55] text-muted-foreground">
          {message}
        </p>
      </div>
      {onRetry && status !== 403 && status !== 404 && status !== 401 ? (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <Icon icon={Refresh01Icon} className="size-3.5" />
          Try again
        </Button>
      ) : null}
    </div>
  );
}

/**
 * A page that is still loading.
 *
 * It draws the shape of what is coming — a title, a row of numbers, a list —
 * rather than a spinner, so the layout does not jump when the data lands. The
 * router only shows it after 400ms (`defaultPendingMs`), because against the
 * fixtures everything resolves in under a frame and a skeleton that flashes is
 * worse than no skeleton at all.
 */
export function PageSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col gap-6 px-6 pt-6 pb-12"
    >
      <span className="sr-only">Loading</span>
      <div className="flex flex-col gap-2">
        <Skeleton className="h-7 w-52" />
        <Skeleton className="h-4 w-[34rem] max-w-full" />
      </div>
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="flex flex-col gap-2 bg-card px-5 py-4">
            <Skeleton className="h-3.5 w-24" />
            <Skeleton className="h-7 w-16" />
          </div>
        ))}
      </div>
      <div className="flex flex-col overflow-hidden rounded-lg border bg-card">
        <div className="border-b px-4 py-3">
          <Skeleton className="h-4 w-32" />
        </div>
        {Array.from({ length: rows }, (_, index) => (
          <div
            key={index}
            className="flex items-center gap-3 border-b px-4 py-3 last:border-0"
          >
            <Skeleton className="size-7 shrink-0 rounded-md" />
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-20 shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** The two-pane screens load a list, not a page of panels. */
export function ListSkeleton({ rows = 10 }: { rows?: number }) {
  return (
    <div role="status" aria-live="polite" className="flex flex-col gap-2 p-3">
      <span className="sr-only">Loading</span>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex flex-col gap-1.5 px-1 py-2">
          <Skeleton className="h-3.5 w-2/3" />
          <Skeleton className="h-3 w-full" />
        </div>
      ))}
    </div>
  );
}

/** The 404 body, shared by the root route and the not-found screens. */
export function NotFoundState({
  title = "That page does not exist",
  description,
  backTo,
  backLabel = "Back to the dashboard",
}: {
  title?: string;
  description?: ReactNode;
  backTo?: ReactNode;
  backLabel?: string;
}) {
  return (
    <main className="flex min-h-svh flex-col items-center justify-center gap-4 px-6 text-center">
      <p className="font-mono text-[11px] tracking-[0.14em] text-muted-foreground uppercase">
        404
      </p>
      <h1 className="text-[22px] font-semibold tracking-[-0.015em]">{title}</h1>
      {description ? (
        <p className="max-w-[48ch] text-[13.5px] leading-[1.55] text-muted-foreground">
          {description}
        </p>
      ) : null}
      {backTo ?? (
        <Button variant="outline" size="sm" asChild>
          <AppLink to="/">
            <Icon icon={ArrowLeft01Icon} className="size-3.5" />
            {backLabel}
          </AppLink>
        </Button>
      )}
    </main>
  );
}
